import { format, addDays, startOfDay } from 'date-fns';
import type { AvailableTime } from '../BookCalendar';
import { MarkedDates } from 'react-native-calendars/src/types';
import {
  CALENDAR_DAY_FORMAT,
  CALENDAR_MONTH_FORMAT,
} from '../shared/constants';
import { AVAILABILITY_HORIZON_DAYS } from '@/shared/constants/availability';

/**
 * Normalize a Date to local midnight and return 'YYYY-MM-DD'.
 */
export function toCalendarDateString(date: Date): string {
  return format(startOfDay(date), CALENDAR_DAY_FORMAT);
}

/**
 * Determine the calendar window bounds based on availability or fallback.
 */
export function getWindowBounds(
  availableTimes: AvailableTime[],
  loading: boolean,
): { minDate: Date; maxDate: Date; currentMonth: string } {
  // Always use local midnight for today
  const today = startOfDay(new Date());

  // Fallback to the full booking-horizon window starting today
  if (loading || availableTimes.length === 0) {
    const fallbackMin = today;
    const fallbackMax = addDays(today, AVAILABILITY_HORIZON_DAYS);

    return {
      minDate: fallbackMin,
      maxDate: fallbackMax,
      currentMonth: format(fallbackMin, CALENDAR_MONTH_FORMAT),
    };
  }

  // Compute true bounds from availableTimes
  // These times should be sorted by date, so we can just take the first and last
  const first = startOfDay(availableTimes[0].value);
  const last = startOfDay(availableTimes[availableTimes.length - 1].value);

  return {
    minDate: first,
    maxDate: last,
    currentMonth: format(first, CALENDAR_MONTH_FORMAT),
  };
}

/**
 * Build a map of all dates in [start..end] marked as disabled.
 */
export function buildDisabledMap(start: Date, end: Date): MarkedDates {
  const map: MarkedDates = {};
  for (let day = start; day <= end; day = addDays(day, 1)) {
    const dateString = toCalendarDateString(day);
    map[dateString] = { disabled: true, disableTouchEvent: true };
  }
  return map;
}

/**
 * Overlay available times onto the disabled map to enable those dates.
 */
export function applyAvailability(
  map: MarkedDates,
  availableTimes: AvailableTime[],
) {
  availableTimes.forEach(({ value }) => {
    const dateString = toCalendarDateString(value);
    map[dateString] = { disabled: false, disableTouchEvent: false };
  });
}

/**
 * Mark a selected date in the map if it falls within the enabled window.
 */
export function markSelectedDay(map: MarkedDates, selectedDate?: Date) {
  if (!selectedDate) return;

  const dateString = toCalendarDateString(selectedDate);
  if (map[dateString] && !map[dateString].disabled) {
    map[dateString] = {
      ...map[dateString],
      selected: true,
    };
  }
}

/**
 * Check if a given date falls within the availableTimes list (by comparing date strings).
 */
export function isDateInAvailableTimes(
  date: Date,
  availableTimes: AvailableTime[],
): boolean {
  const dateString = toCalendarDateString(date);
  return availableTimes.some(
    ({ value }) => toCalendarDateString(startOfDay(value)) === dateString,
  );
}

/**
 * Get the first selectable date at local midnight from availableTimes.
 */
export function getFirstSelectableDate(
  availableTimes: AvailableTime[],
): Date | undefined {
  if (availableTimes.length === 0) return undefined;
  return startOfDay(availableTimes[0].value);
}
